"use client";

import { Compass, Users, Calendar, TrendingUp, Trophy } from "lucide-react";
import { CtaBand, RelatedLinks } from "@/components/marketing/site-ui";
import { useSiteContent } from "@/components/marketing/use-site-content";
import { useTranslations } from "@/hooks/use-translations";

/**
 * The page that answers "what is this and what do I actually do here".
 *
 * Tom's point, and it was the right one: someone lands on GrowthSync, sees a lot of
 * features, and has no idea where to start or what the platform is FOR. So this is
 * deliberately written as verbs, not features. Every section says what YOU do and what
 * comes back, because "communities" means nothing on its own.
 *
 * Laid out as an alternating list rather than a grid of identical cards: five things that
 * look the same in five boxes reads as filler, and nobody remembers any of them.
 */

const CAPABILITIES = [
  { icon: Compass, key: "discover" },
  { icon: Users, key: "communities" },
  { icon: Calendar, key: "events" },
  { icon: TrendingUp, key: "opportunities" },
  { icon: Trophy, key: "momentum" },
] as const;

const FIRST_WEEK = ["one", "two", "three"] as const;

export function HowItWorksClient() {
  const { t } = useTranslations("guide");
  const c = useSiteContent();

  return (
    /* The site shell (app/(site)/layout.tsx) owns the page chrome and the <main> landmark
       now, so this renders only its own sections. It used to carry a header and no footer,
       which left the page with no way out except the back button.

       Every section sits on the plain site grid, and the measure is set on the text inside it.
       Until 2026-08-24 the sections carried max-w-3xl, max-w-4xl and max-w-3xl again on the
       centring wrapper itself, so the left edge of the page moved by 70px between one section
       and the next, and the last one was centre-aligned on top of that. */
    <div>
      <div>
        <section className="relative overflow-hidden pb-16 pt-14 md:pb-20 md:pt-20">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
            style={{
              background:
                "radial-gradient(60% 100% at 30% 0%, rgba(33,122,246,0.10) 0%, rgba(33,122,246,0.04) 45%, rgba(250,250,250,0) 78%)",
            }}
          />
          <div className="gs-page relative">
            <div className="max-w-[58rem]">
              <p className="gs-eyebrow text-primary">{t("hero.eyebrow")}</p>
              <h1 className="gs-headline mt-3 text-balance text-neutral-900">{t("hero.heading")}</h1>
              <p className="gs-lede mt-5 text-pretty text-neutral-600">{t("hero.body")}</p>
            </div>
          </div>
        </section>

        <section className="border-y border-neutral-200 bg-white py-14 md:py-18">
          <div className="gs-page">
            <div className="max-w-[58rem]">
              <h2 className="gs-h1 text-neutral-900">{t("who.heading")}</h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-700 md:text-lg">{t("who.body")}</p>
              <p className="mt-4 text-base leading-relaxed text-neutral-700 md:text-lg">{t("who.notFor")}</p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="gs-page">
            <h2 className="gs-headline max-w-[20ch] text-balance text-neutral-900">{t("does.heading")}</h2>
            <p className="mt-4 gs-measure text-base leading-relaxed text-neutral-600 md:text-lg">
              {t("does.body")}
            </p>

            <ul className="mt-12 max-w-[58rem] space-y-12" role="list">
              {CAPABILITIES.map(({ icon: Icon, key }) => (
                <li key={key} className="grid gap-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-7">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl gradient-primary text-white shadow-[0_8px_20px_-8px_rgba(11,99,224,0.6)]">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="gs-h1 text-neutral-900">{t(`does.${key}.title`)}</h3>
                    {/* The action first, then what comes back from it. */}
                    <p className="mt-2 text-base leading-relaxed text-neutral-700 md:text-lg">
                      {t(`does.${key}.body`)}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-primary">{t(`does.${key}.result`)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-y border-neutral-200 bg-white py-16 md:py-24">
          <div className="gs-page">
            <div className="max-w-[58rem]">
              <h2 className="gs-headline text-neutral-900">{t("week.heading")}</h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">{t("week.body")}</p>

              {/* A path with a line running through it, rather than three identical boxes. */}
              <ol className="mt-10 space-y-8 border-l-2 border-primary/20 pl-7" role="list">
                {FIRST_WEEK.map((step) => (
                  <li key={step} className="relative">
                    <span
                      aria-hidden="true"
                      className="absolute -left-[2.3rem] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-primary shadow"
                    />
                    <p className="text-base font-bold text-neutral-900 md:text-lg">{t(`week.${step}.title`)}</p>
                    <p className="mt-1.5 text-base leading-relaxed text-neutral-700">{t(`week.${step}.body`)}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <RelatedLinks
          links={[
            { href: "/features/discover", title: c.features.discover.name, body: c.features.discover.tagline },
            { href: "/features/events", title: c.features.events.name, body: c.features.events.tagline },
            { href: "/pricing", title: c.nav.pricing, body: c.pricingPage.short },
          ]}
        />

        <div className="pt-14 md:pt-20">
          <CtaBand heading={t("cta.heading")} body={t("cta.body")} primaryLabel={t("cta.button")} />
        </div>

      </div>
    </div>
  );
}
